export type RsvpResponse = "going" | "maybe" | "not_going";
export type AttendancePreStatus = "attend" | "maybe" | "absent";

export type FeeReminderKind =
  | "before_due"
  | "on_due"
  | "overdue_1"
  | "overdue_7"
  | "overdue_14"
  | "overdue_30";

export type VenuePaymentMode = "stripe_test" | "pay_at_venue";
export type PasswordResetChannel = "email" | "linked_parent";

export const VENUE_HOLD_MINUTES = 10;
export const FEE_REMINDER_BEFORE_DUE_DAYS = 3;

const DAY_MS = 24 * 60 * 60 * 1000;

function utcDay(value: Date): number {
  return Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate());
}

/** One reminder before the due date, one on the due date, then 1, 7, 14, and 30 days after it. */
export function feeReminderKind(due: Date, today: Date): FeeReminderKind | null {
  const diff = Math.round((utcDay(today) - utcDay(due)) / DAY_MS);
  if (diff === -FEE_REMINDER_BEFORE_DUE_DAYS) return "before_due";
  if (diff === 0) return "on_due";
  if (diff === 1) return "overdue_1";
  if (diff === 7) return "overdue_7";
  if (diff === 14) return "overdue_14";
  if (diff === 30) return "overdue_30";
  return null;
}

export function rsvpToAttendance(response: RsvpResponse): AttendancePreStatus {
  if (response === "going") return "attend";
  if (response === "maybe") return "maybe";
  return "absent";
}

export function attendanceToRsvp(status: string | null | undefined): RsvpResponse | null {
  if (status === "attend" || status === "going") return "going";
  if (status === "maybe") return "maybe";
  if (status === "absent" || status === "not_going") return "not_going";
  return null;
}

export function holdExpiresAt(now: Date): Date {
  return new Date(now.getTime() + VENUE_HOLD_MINUTES * 60 * 1000);
}

export function isHoldActive(expiresAt: Date, now: Date): boolean {
  return expiresAt.getTime() > now.getTime();
}

/**
 * Stripe checkout is used only when the existing secret is a test key and a
 * venue price id is already configured. Live keys stay on pay-at-venue.
 */
export function venuePaymentMode(input: {
  stripeSecretKey?: string | null;
  venuePriceId?: string | null;
}): VenuePaymentMode {
  const key = input.stripeSecretKey?.trim() ?? "";
  const price = input.venuePriceId?.trim() ?? "";
  if (key.startsWith("sk_test_") && price.startsWith("price_")) return "stripe_test";
  return "pay_at_venue";
}

export function isGeneratedChildMailbox(email: string): boolean {
  const value = email.trim().toLowerCase();
  return /^child_[^@]+@fanjago\.com$/.test(value) || /^\d+@fanjago\.com$/.test(value);
}

export function isGeneratedParentMailbox(email: string): boolean {
  return /^parent_[^@]+@fanjago\.com$/.test(email.trim().toLowerCase());
}

export function isGeneratedMailbox(email: string): boolean {
  return isGeneratedChildMailbox(email) || isGeneratedParentMailbox(email);
}

/** Child placeholder mailboxes are reset by the linked parent. Parents use email. */
export function passwordResetChannel(email: string): PasswordResetChannel {
  if (isGeneratedChildMailbox(email)) return "linked_parent";
  return "email";
}

export function isFreeEvent(price: number | null | undefined): boolean {
  if (price == null || Number.isNaN(Number(price))) return true;
  return Number(price) <= 0;
}

export function eventTicketCode(random: string): string {
  const cleaned = random.replace(/[^a-zA-Z0-9]/g, "").slice(0, 12).toUpperCase();
  if (cleaned.length < 8) throw new Error("Ticket code source is too short");
  return `FJEVT-${cleaned}`;
}

export interface NotificationRow {
  title: string;
  message: string;
  quick_action_data?: unknown;
}

function i18nPayload(data: unknown): { key: string; params: Record<string, unknown> } | null {
  if (!data || typeof data !== "object") return null;
  const record = data as { i18nKey?: unknown; i18nParams?: unknown };
  if (typeof record.i18nKey !== "string" || record.i18nKey.length === 0) return null;
  const params =
    record.i18nParams && typeof record.i18nParams === "object"
      ? (record.i18nParams as Record<string, unknown>)
      : {};
  return { key: record.i18nKey, params };
}

/**
 * Prefer the viewer's i18n catalog when the row carries a key. Stored title
 * and message stay as the single-language fallback.
 */
export function notificationDisplay(
  notification: NotificationRow,
  translate: (key: string, params?: Record<string, unknown>) => string,
): { title: string; message: string } {
  const payload = i18nPayload(notification.quick_action_data);
  if (!payload) return { title: notification.title, message: notification.message };

  const title = translate(`${payload.key}.title`, payload.params);
  const message = translate(`${payload.key}.message`, payload.params);
  if (title === `${payload.key}.title` || message === `${payload.key}.message`) {
    return { title: notification.title, message: notification.message };
  }
  return { title, message };
}

export function nextWeekday(dayOfWeek: number, from: Date): Date {
  const date = new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), from.getUTCDate()));
  const delta = (dayOfWeek - date.getUTCDay() + 7) % 7;
  date.setUTCDate(date.getUTCDate() + (delta === 0 ? 0 : delta));
  return date;
}
